
import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFavorites } from "@/hooks/useFavorites";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface FavoriteButtonProps {
  serviceId: number;
}

export const FavoriteButton = ({ serviceId }: FavoriteButtonProps) => {
  const { isFavorite, addFavorite, removeFavorite, isSyncing, isAuthenticated } = useFavorites();
  const [isFav, setIsFav] = useState(false);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      setIsLoading(false);
      
      if (data.session) {
        setIsFav(isFavorite(serviceId));
      }
    };
    
    checkAuth();
  }, [serviceId, isFavorite]);

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please sign in to add favorites",
        variant: "destructive",
      });
      return;
    }

    if (isFav) {
      const success = await removeFavorite(serviceId);
      if (success) {
        setIsFav(false);
      }
    } else {
      const success = await addFavorite(serviceId);
      if (success) {
        setIsFav(true);
      }
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleToggleFavorite}
      className="h-8 w-8 rounded-full hover:bg-gray-100"
      disabled={isSyncing}
      aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
    >
      <Heart 
        className={`h-5 w-5 ${isFav ? "fill-red-500 text-red-500" : "text-gray-500"}`} 
      />
    </Button>
  );
};
